/** @typedef {import('headlight-configurable-application').Pict} Pict */

const libPictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;
const ScopedEntityData = require('./_ScopedEntityData.js');

// CSS for the Headlight "hide column in PDF only" toggle injected into each row of the
// pict-section-form ColumnChooser popover (opted-in groups only). The eye icon is the
// PDF visibility state; the row's existing checkbox remains the interactive (HTML) visibility.
const _PDFColumnChooserCSS = /*css*/`
	.pict-tabular-colchooser-row.has-pdf-toggle { justify-content: space-between; }
	.pict-tabular-colchooser-row.has-pdf-toggle .colchooser-html-label { display: inline-flex; align-items: center; gap: 0.5rem; flex: 1 1 auto; min-width: 0; cursor: pointer; }
	.pict-tabular-colchooser-row.has-pdf-toggle .colchooser-html-label span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.colchooser-pdf-toggle { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 0.1rem; cursor: pointer;
		border: 1px solid transparent; background: transparent; border-radius: 6px; padding: 0.1rem 0.3rem;
		color: var(--theme-color-text-muted, #6b7686); font-size: 0.8rem; }
	.colchooser-pdf-toggle:hover:not([disabled]) { background: var(--theme-color-background-tertiary, #eceef2); color: var(--theme-color-text-primary, #1f2733); }
	.colchooser-pdf-toggle.is-pdf-hidden { color: var(--bulma-danger, #c0392b); }
	.colchooser-pdf-toggle[disabled] { opacity: 0.4; cursor: not-allowed; }
	.colchooser-pdf-toggle .icon { margin: 0; }
`;

// Theme tokens aren't loaded on form surfaces, so the light fallbacks above would show in dark mode. Same three dark
// signals as pict-section-form's ColumnChooser dark rules (Pict-Layout-Tabular.js); keep the two in step.
const _PDFColumnChooserDarkRules = (pScope) => /*css*/`
	${pScope} .colchooser-pdf-toggle { color: var(--theme-color-text-muted, #8b95a4); }
	${pScope} .colchooser-pdf-toggle:hover:not([disabled]) { background: var(--theme-color-background-tertiary, #2c3542); color: var(--theme-color-text-primary, #e6e9ee); }
`;
const _PDFColumnChooserDarkCSS = _PDFColumnChooserDarkRules('[data-theme="dark"]')
	+ _PDFColumnChooserDarkRules('.theme-dark')
	+ `@media (prefers-color-scheme: dark) {` + _PDFColumnChooserDarkRules('html:not([data-theme="light"])') + `}`;

// A group with `GroupExtra: "Hide Table Controls"` still reserved the height of the
// controls row (~21px of dead space under the last data row). The wrapper gets
// `.hide-table-controls`, but the existing rule in bulma-table.scss only reaches
// elements carrying `[data-table-controls]` -- and the controls ROW and its two
// `.column` children carry no such attribute, so they kept rendering.
//
// Collapse the row, but ONLY while the scroll slider inside it is hidden. That
// slider is deliberately exempt from `.hide-table-controls` so a WIDE table with
// controls hidden can still be scrolled; blanket-hiding the row would take that
// away. `:has()` re-evaluates when prepareAnyExtraTableControls() reveals the
// slider, so the row returns exactly when it is needed.
//
// Lives here rather than in headlight-styles.scss so the fix ships with the bundle
// rebuild instead of regenerating headlight-styles.css, which has to be coordinated
// with the in-flight Select2 -> Pict-Section-Picker migration.
const _HiddenTableControlsCSS = /*css*/`
	.hide-table-controls .columns:has([data-table-controls="scroll-slider"].is-hidden) { display: none; }
`;


// ─────────────────────────────────────────────────────────────────────────
// Entry controls (opt-in per group via `Group.EntryControls`)
//
// Long Tabular / RecordSet groups are frequently several viewport-heights
// tall, and the only "Add" button lives BELOW the whole group -- so adding an
// entry means scrolling to the bottom and back. `EntryControls` lets a form
// author put the add bar at the top, open the new entry in a modal, and/or
// prepend rather than append. Every knob is opt-in: a group with no
// `EntryControls` renders exactly the markup it did before this existed.
//
// NOTE: `EntryControls.Position` is the ADD BAR. It is orthogonal to the
// pict-section-form group option `EditingControlsPosition`, which positions
// the per-row del/up/down menu COLUMN (left/right/hidden). Similar names,
// different controls.
// ─────────────────────────────────────────────────────────────────────────
const _EntryControlsDefaults =
{
	Position: 'bottom',
	EntryPoint: 'end',
	Mode: 'inline',
	AllowModeToggle: false,
	RowThreshold: 10,
	ViewportMultiple: 3,
	ModalTitle: '',
	ModalWidth: 'min(1100px, 92vw)',
	ModalColumns: 1,
};

// localStorage key prefix for the per-user inline/modal preference. Bump the
// version segment if the stored shape ever changes.
const _ENTRY_MODE_STORAGE_PREFIX = 'Headlight.EntryMode.v1';

const _EntryControlsCSS = /*css*/`
	.pict-entry-mode-toggle { margin-right: 0.5rem; }
	.pict-entry-modal-body { max-height: min(70vh, 720px); overflow-y: auto; }
	.pict-entry-modal-body .columns { margin: 0; }
	.pict-entry-modal-body .column { padding: 0.4rem 0.6rem; }
	.pict-entry-modal-body .label { margin-bottom: 0.25rem; }
	.pict-entry-modal-body .help { margin-top: 0.15rem; }
	.pict-entry-modal-field--chooser-hidden > .label::after { content: ' (hidden in table)'; font-weight: 400; opacity: 0.7; font-size: 0.8em; }
`;

// Escape a manifest-authored string for safe interpolation into a template's
// text node. The value comes from form config rather than user data, but these
// templates are assembled as HTML strings, so escaping is the mitigation --
// see the same idiom in HeadlightDynamicInputsBundle's {~LabelWithChips:~}.
const _escapeTemplateText = (pString) => String(pString == null ? '' : pString)
	.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

class CustomInputHandler extends libPictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {import('pict')} */
		this.pict;
		/** @type {import('pict') & { HeadlightPopover: any }} */
		this.fable;
		/** @type {any} */
		this.log;

		this.pict.MetaTemplate.addPattern('{~HideTableControls:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpDataObject = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			let hideTableControls = false;
			if (tmpDataObject && tmpDataObject?.hasOwnProperty('GroupExtra') && tmpDataObject.GroupExtra)
			{
				// if we find 'HideTableControls' or 'Hide Table Controls' in the GroupExtra string, hide the table controls manually
				if ([ 'HideTableControls', 'Hide Table Controls' ].some((i) => tmpDataObject.GroupExtra.includes(i)))
				{
					hideTableControls = true;
				}
			}
			else if (tmpDataObject.MinimumRowCount && tmpDataObject.MaximumRowCount)
			{
				hideTableControls = tmpDataObject.MinimumRowCount >= tmpDataObject.MaximumRowCount;
			}
			return (hideTableControls) ? 'hide-table-controls' : '';
		});

		// EditingControlsPosition 'hidden' has no menu column to toggle, so hide the
		// toggle button (Bulma 'is-hidden'). Record is the group descriptor.
		this.pict.MetaTemplate.addPattern('{~HideMenuColumnToggle:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpDataObject = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			if (tmpDataObject && (tmpDataObject.EditingControlsPosition === 'hidden'))
			{
				return 'is-hidden';
			}
			return '';
		});

		// Per-group copy for the tabular "no rows" placeholder row. The row itself
		// and its show/hide are not ours -- the Group-Postfix template always emits
		// `tr.table-emtpy-row` (sic) last in the tbody and bulma-table.scss hides it
		// via `:not(:first-child)`. Only the WORDING was a hardcoded literal, so a
		// manifest had no way to explain why a particular table is empty.
		//
		// Body is `<address>:<default copy>`, split on the FIRST colon -- the default
		// is each theme's existing literal, so a group that does not opt in renders
		// byte-identically to before this macro existed.
		this.pict.MetaTemplate.addPattern('{~TabularEmptyMessage:', '~}', (pHash, pData, fCallback) =>
		{
			return this.resolveTabularEmptyMessage(pHash, pData, fCallback);
		});

		// 'left' controls: the framework's {~TEC:~} cell emits bare del/up/down links;
		// override it to reuse our right-side menu button so both positions match.
		this.overrideTabularEditingControlsRenderer();

		// Register CSS for the PDF-only column-hide toggle injected into the chooser popover.
		if (this.pict && this.pict.CSSMap && typeof this.pict.CSSMap.addCSS === 'function')
		{
			this.pict.CSSMap.addCSS('Headlight-Tabular-PDFColumnChooser-CSS', _PDFColumnChooserCSS + _PDFColumnChooserDarkCSS, 500);
			this.pict.CSSMap.addCSS('Headlight-Tabular-HiddenControls-CSS', _HiddenTableControlsCSS, 500);
		}

		// "Hide column in PDF (Print theme) only" — opt-in via group ColumnChooser.SelectPDFOnlyColumns.
		this.overrideColumnChooserForPDF();

		// ── Entry controls ────────────────────────────────────────────────
		// These three macros are registered HERE ONLY, never also in
		// Bulma-RecordSetControls: both providers are always attached together
		// and a second addPattern() on the same pattern silently shadows the
		// first. The RecordSet templates consume the same macros.

		// Top add bar: hidden unless the group opted into 'top' or 'both'.
		this.pict.MetaTemplate.addPattern('{~HideTopEntryControls:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpConfig = this.getEntryControlsConfig(this.pict.resolveStateFromAddress(pHash, pData, fCallback));
			return (tmpConfig && ((tmpConfig.Position === 'top') || (tmpConfig.Position === 'both'))) ? '' : 'is-hidden';
		});

		// Bottom add bar: visible unless the group explicitly moved it to 'top'.
		// The no-config case MUST return '' -- that is what keeps every existing
		// form rendering exactly as it does today.
		//
		// EXCEPT on a read-only table. `EditingControlsPosition: 'hidden'` already
		// removes the per-row del/up/down column and the menu-column toggle; a table
		// with no row controls is a report, and offering "Add new row" underneath it
		// is incoherent -- a reader clicks it and gets a blank row on a dashboard
		// that cannot save. The two express one intent, so the one flag drives both
		// rather than adding a second key nobody would remember to set.
		this.pict.MetaTemplate.addPattern('{~HideBottomEntryControls:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpGroup = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			if (tmpGroup && (tmpGroup.EditingControlsPosition === 'hidden'))
			{
				return 'is-hidden';
			}
			const tmpConfig = this.getEntryControlsConfig(tmpGroup);
			return (tmpConfig && (tmpConfig.Position === 'top')) ? 'is-hidden' : '';
		});

		// The inline/modal preference toggle. Emits nothing at all unless the
		// group set AllowModeToggle; when it does emit it starts hidden and
		// refreshEntryModeToggleVisibility() reveals it once the group is big
		// enough to be painful.
		// The whole opt-in TOP add bar (add button + mode toggle), or '' when the
		// group never opted in -- so untouched forms emit no extra markup.
		this.pict.MetaTemplate.addPattern('{~EntryAddBarTop:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpGroup = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			// Same reasoning as the bottom bar: a read-only table has nothing to add to.
			if (tmpGroup && (tmpGroup.EditingControlsPosition === 'hidden'))
			{
				return '';
			}
			return this.renderEntryAddBarHTML(tmpGroup);
		});

		this.pict.MetaTemplate.addPattern('{~EntryModeToggleButton:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpGroup = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			return this.renderEntryModeToggleHTML(tmpGroup);
		});

		// Column classes for one field cell inside the entry modal. Record is the
		// COLUMN DESCRIPTOR; buildModalRowTemplate stamps the two flags read here.
		// The modal deliberately does not honour per-descriptor PictForm.Width --
		// it is a stacked entry form, not the grid.
		this.pict.MetaTemplate.addPattern('{~EntryModalCellClass:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpInput = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			const tmpPictForm = (tmpInput && tmpInput.PictForm) ? tmpInput.PictForm : {};
			let tmpClasses = tmpPictForm._EntryModalHalfWidth ? 'is-6' : 'is-12';
			if (tmpPictForm._EntryModalChooserHidden)
			{
				tmpClasses += ' pict-entry-modal-field--chooser-hidden';
			}
			return tmpClasses;
		});

		if (this.pict && this.pict.CSSMap && typeof this.pict.CSSMap.addCSS === 'function')
		{
			this.pict.CSSMap.addCSS('Headlight-Tabular-EntryControls-CSS', _EntryControlsCSS, 500);
		}

		// While a modal entry is staged, suppress the tabular layout's mid-marshal
		// rebuild so the staged row never gets an inline DOM twin.
		this.overrideTabularMarshalForModalEntry();

		// The staged-entry record, or null. Only one modal entry at a time (the
		// modal itself is a singleton overlay).
		/** @type {{ViewHash: string, GroupIndex: number, StagedIndex: number, Committed: boolean, Dialog: any}|null} */
		this._modalEntryState = null;
	}

	/**
	 * Override the Tabular layout's left-controls renderer ({~TEC:~}) to use our menu
	 * button. Idempotent; no-op if the layout provider isn't registered.
	 */
	overrideTabularEditingControlsRenderer()
	{
		const tmpTabularLayout = this.pict.providers['Pict-Layout-Tabular'];
		if (!tmpTabularLayout ||
			(typeof tmpTabularLayout._renderTabularEditingControlsHTML !== 'function') ||
			tmpTabularLayout._headlightEditingControlsOverridden)
		{
			return;
		}
		const tmpSelf = this;
		tmpTabularLayout._headlightEditingControlsOverridden = true;
		tmpTabularLayout._renderTabularEditingControlsHTML = function(pView, pGroupIndex, pRowKey)
		{
			return tmpSelf.renderTabularEditingControlsMenuCell(pView, pGroupIndex, pRowKey);
		};
	}

	// ─────────────────────────────────────────────────────────────────────────
	// "Hide column in PDF (Print theme) only"
	//
	// pict-section-form's ColumnChooser hides columns by NOT rendering them, driven
	// by a form-data array of column hashes at `<GroupHash>_HiddenColumns` (read via
	// Pict-Layout-Tabular._getTabularColumnChooserHiddenSet). That array hides a
	// column in EVERY theme. This Headlight feature adds a SECOND, parallel array,
	// `<GroupHash>_PDFHiddenColumns`, whose columns are hidden ONLY in the Print
	// theme — and unions it into the effective hidden set at render time when (and
	// only when) we are rendering non-interactively. Because the union always
	// includes the base (interactive) hidden set, an interactively-hidden column is
	// necessarily hidden in the PDF too (HTML-hide cascades to PDF; PDF-hide is the
	// lower-priority, additive layer). Nothing is written to pict-section-form.
	//
	// Opt-in: a tabular group sets `ColumnChooser: { SelectPDFOnlyColumns: true }`.
	// Per-column default: a descriptor sets `PictForm.TabularPDFDefaultHidden: true`.
	// ─────────────────────────────────────────────────────────────────────────

	/**
	 * The absolute form-data address of a group's PDF-only hidden-column-hash array.
	 * Mirrors Pict-Layout-Tabular._getTabularHiddenColumnsAddress; defaults to
	 * `<GroupHash>_PDFHiddenColumns`, overridable via ColumnChooser.PDFDataAddress.
	 * @param {Object} pView - The form view.
	 * @param {Object} pGroup - The tabular group descriptor.
	 * @return {string} - The absolute address.
	 */
	getPDFHiddenColumnsAddress(pView, pGroup)
	{
		const tmpChooser = (pGroup && pGroup.ColumnChooser) || {};
		const tmpDataAddress = (typeof tmpChooser.PDFDataAddress === 'string' && tmpChooser.PDFDataAddress.length > 0) ?
			tmpChooser.PDFDataAddress :
			`${pGroup.Hash}_PDFHiddenColumns`;
		return `${pView.getMarshalDestinationAddress()}.${tmpDataAddress}`;
	}

	/**
	 * The set of column hashes hidden-in-PDF BY DEFAULT for a group: the group's
	 * `ColumnChooser.PDFDefaultHiddenColumns` array plus every choosable descriptor
	 * flagged `PictForm.TabularPDFDefaultHidden`. Mirrors the framework's
	 * `_getTabularColumnChooserDefaultHidden` (which merges `DefaultHiddenColumns`
	 * with per-descriptor `TabularDefaultHidden`). Applies only until the user makes
	 * an explicit choice (which writes an array to form data).
	 * @param {Object} pGroup - The tabular group descriptor.
	 * @return {Array<string>} - Array of column hashes.
	 */
	getPDFDefaultHidden(pGroup)
	{
		const tmpDefaults = {};
		// group-level list (raw ColumnChooser config, same place SelectPDFOnlyColumns lives)
		const tmpChooser = (pGroup && pGroup.ColumnChooser) || {};
		if (Array.isArray(tmpChooser.PDFDefaultHiddenColumns))
		{
			for (let i = 0; i < tmpChooser.PDFDefaultHiddenColumns.length; i++)
			{
				tmpDefaults[String(tmpChooser.PDFDefaultHiddenColumns[i])] = true;
			}
		}
		// per-descriptor flags
		const tmpManifest = pGroup && pGroup.supportingManifest;
		if (tmpManifest && Array.isArray(tmpManifest.elementAddresses))
		{
			for (let k = 0; k < tmpManifest.elementAddresses.length; k++)
			{
				const tmpHash = String(tmpManifest.elementAddresses[k]);
				const tmpDescriptor = tmpManifest.elementDescriptors[tmpHash];
				if (tmpDescriptor && tmpDescriptor.PictForm &&
					!tmpDescriptor.PictForm.TabularHidden &&
					tmpDescriptor.PictForm.TabularPDFDefaultHidden === true)
				{
					tmpDefaults[tmpHash] = true;
				}
			}
		}
		return Object.keys(tmpDefaults);
	}

	/**
	 * The EFFECTIVE set of PDF-only-hidden column hashes for a group: the array
	 * stored in form data when the user has chosen, otherwise the configured
	 * defaults. This is the explicit "hide in PDF" layer only — it does NOT include
	 * the interactive hidden set (the union happens in the layout override).
	 * @param {Object} pView - The form view.
	 * @param {Object} pGroup - The tabular group descriptor.
	 * @return {Set<string>} - Set of column hashes.
	 */
	getPDFHiddenColumnsSet(pView, pGroup)
	{
		const tmpStored = this.pict.resolveStateFromAddress(this.getPDFHiddenColumnsAddress(pView, pGroup));
		const tmpList = Array.isArray(tmpStored) ? tmpStored : this.getPDFDefaultHidden(pGroup);
		const tmpSet = new Set();
		for (let i = 0; i < tmpList.length; i++)
		{
			tmpSet.add(String(tmpList[i]));
		}
		return tmpSet;
	}

	/**
	 * Union the PDF-only hidden columns into a base (interactive) hidden set. Returns
	 * a NEW set so the caller's base set is never mutated.
	 * @param {Object} pView - The form view.
	 * @param {Object} pGroup - The tabular group descriptor.
	 * @param {Set<string>} pBaseSet - The interactive hidden set.
	 * @return {Set<string>} - base ∪ PDF-only.
	 */
	unionPDFHiddenColumns(pView, pGroup, pBaseSet)
	{
		const tmpResult = new Set(pBaseSet);
		const tmpPDFSet = this.getPDFHiddenColumnsSet(pView, pGroup);
		tmpPDFSet.forEach((pHash) => tmpResult.add(pHash));
		return tmpResult;
	}

	/**
	 * Install the two Headlight overrides on the singleton Pict-Layout-Tabular
	 * provider (same monkey-patch pattern as overrideTabularEditingControlsRenderer):
	 *   1. _getTabularColumnChooserHiddenSet — union the PDF-only set when rendering
	 *      non-interactively (Print theme) for opted-in groups.
	 *   2. _renderTabularColumnChooserPopover — add a per-row PDF-hide toggle to the
	 *      chooser menu for opted-in groups.
	 * Idempotent; no-op if the layout provider isn't registered.
	 */
	overrideColumnChooserForPDF()
	{
		const tmpLayout = this.pict.providers['Pict-Layout-Tabular'];
		if (!tmpLayout ||
			(typeof tmpLayout._getTabularColumnChooserHiddenSet !== 'function') ||
			(typeof tmpLayout._renderTabularColumnChooserPopover !== 'function') ||
			tmpLayout._headlightPDFColumnChooserOverridden)
		{
			return;
		}
		const tmpSelf = this;
		tmpLayout._headlightPDFColumnChooserOverridden = true;

		// 1) Union PDF-only hidden columns into the effective hidden set — Print theme only.
		const fOriginalHiddenSet = tmpLayout._getTabularColumnChooserHiddenSet;
		tmpLayout._getTabularColumnChooserHiddenSet = function(pView, pGroup)
		{
			const tmpBaseSet = fOriginalHiddenSet.call(this, pView, pGroup);
			// Falsy base = chooser disabled (preserve legacy path). Interactive = no PDF
			// hiding. No opt-in = feature dormant. In all three, return the base untouched.
			if (!tmpBaseSet ||
				tmpSelf.pict.AppData.Interactive ||
				!(pGroup && pGroup.ColumnChooser && pGroup.ColumnChooser.SelectPDFOnlyColumns))
			{
				return tmpBaseSet;
			}
			return tmpSelf.unionPDFHiddenColumns(pView, pGroup, tmpBaseSet);
		};

		// 2) Add the per-row PDF-hide toggle to the chooser popover — opted-in groups only.
		const fOriginalPopover = tmpLayout._renderTabularColumnChooserPopover;
		tmpLayout._renderTabularColumnChooserPopover = function(pView, pGroup)
		{
			if (!(pGroup && pGroup.ColumnChooser && pGroup.ColumnChooser.SelectPDFOnlyColumns))
			{
				return fOriginalPopover.call(this, pView, pGroup);
			}
			return tmpSelf.renderColumnChooserPopoverWithPDF(this, pView, pGroup);
		};

		// 3) "Reset to defaults" must also reset the PDF-only hidden set (not just the
		// interactive hidden set). Write the PDF defaults BEFORE the framework reset so its
		// rebuild + popover repaint already reflect the cleared PDF indicators.
		if (typeof tmpLayout.resetTabularColumnVisibility === 'function')
		{
			const fOriginalReset = tmpLayout.resetTabularColumnVisibility;
			tmpLayout.resetTabularColumnVisibility = function(pViewHash, pGroupIndex)
			{
				const tmpView = tmpSelf.pict.views[pViewHash];
				const tmpGroup = (tmpView && tmpView.sectionDefinition && Array.isArray(tmpView.sectionDefinition.Groups)) ?
					tmpView.sectionDefinition.Groups[Number(pGroupIndex)] :
					null;
				if (tmpGroup && tmpGroup.ColumnChooser && tmpGroup.ColumnChooser.SelectPDFOnlyColumns)
				{
					tmpSelf.pict.setStateValueAtAddress(tmpSelf.getPDFHiddenColumnsAddress(tmpView, tmpGroup), null, tmpSelf.getPDFDefaultHidden(tmpGroup));
				}
				return fOriginalReset.call(this, pViewHash, pGroupIndex);
			};
		}
	}

	/**
	 * Render the ColumnChooser popover for an opted-in group: the framework's
	 * backdrop + panel + reset footer, but each column row carries the existing
	 * HTML show/hide checkbox AND a Headlight PDF-hide toggle. Replicates the
	 * framework markup (Pict-Layout-Tabular._renderTabularColumnChooserPopover) so
	 * the menu looks and behaves identically apart from the extra toggle.
	 * @param {Object} pLayout - The Pict-Layout-Tabular provider (override `this`).
	 * @param {Object} pView - The form view.
	 * @param {Object} pGroup - The tabular group descriptor.
	 */
	renderColumnChooserPopoverWithPDF(pLayout, pView, pGroup)
	{
		const tmpColumns = pLayout._getTabularChoosableColumns(pView, pGroup);
		const tmpPDFSet = this.getPDFHiddenColumnsSet(pView, pGroup);
		let tmpRowsHTML = '';
		for (let i = 0; i < tmpColumns.length; i++)
		{
			const tmpColumn = tmpColumns[i];
			const tmpHTMLHidden = !tmpColumn.Visible;
			const tmpExplicitPDFHidden = tmpPDFSet.has(tmpColumn.Key);
			// Effective: a column hidden interactively is necessarily hidden in PDF.
			const tmpPDFHidden = tmpHTMLHidden || tmpExplicitPDFHidden;
			tmpRowsHTML += this.renderColumnChooserRowHTML(pLayout, pView, pGroup, tmpColumn, tmpHTMLHidden, tmpPDFHidden, tmpExplicitPDFHidden);
		}
		const tmpPopoverHTML = `<div class="pict-tabular-colchooser-backdrop" onclick="_Pict.providers['Pict-Layout-Tabular'].closeTabularColumnChooser('${pView.Hash}', ${pGroup.GroupIndex})"></div>` +
			`<div class="pict-tabular-colchooser-panel">` +
			`<div class="pict-tabular-colchooser-list">${tmpRowsHTML}</div>` +
			`<div class="pict-tabular-colchooser-footer">` +
			`<button type="button" class="pict-tabular-colchooser-reset" onclick="_Pict.providers['Pict-Layout-Tabular'].resetTabularColumnVisibility('${pView.Hash}', ${pGroup.GroupIndex})">Reset to defaults</button>` +
			`</div>` +
			`</div>`;
		this.pict.ContentAssignment.assignContent(`#${pLayout._getTabularColumnChooserElementId(pView, pGroup, 'POPOVER')}`, tmpPopoverHTML);
	}

	/**
	 * Build one chooser row: the framework's HTML show/hide checkbox plus the
	 * Headlight PDF-hide toggle. The PDF toggle is disabled (locked) when the column
	 * is hidden in the interactive view, since that already forces it hidden in PDF.
	 * @param {Object} pLayout - The Pict-Layout-Tabular provider (for _escapeHTML).
	 * @param {Object} pView - The form view.
	 * @param {Object} pGroup - The tabular group descriptor.
	 * @param {{Key: string, Name: string, ColumnIndex: number, Visible: boolean}} pColumn - The choosable column.
	 * @param {boolean} pHTMLHidden - Column hidden in the interactive (HTML) view.
	 * @param {boolean} pPDFHidden - Column effectively hidden in PDF (HTML-hidden OR PDF-hidden).
	 * @param {boolean} pExplicitPDFHidden - Column in the explicit PDF-only hidden set.
	 * @return {string} - The row HTML.
	 */
	renderColumnChooserRowHTML(pLayout, pView, pGroup, pColumn, pHTMLHidden, pPDFHidden, pExplicitPDFHidden)
	{
		const tmpName = pLayout._escapeHTML(pColumn.Name);
		// Same checkbox + handler the framework uses, so interactive show/hide is unchanged.
		const tmpCheckbox = `<label class="colchooser-html-label">` +
			`<input type="checkbox"${pColumn.Visible ? ' checked="checked"' : ''} ` +
			`onchange="_Pict.providers['Pict-Layout-Tabular'].toggleTabularColumnVisibility('${pView.Hash}', ${pGroup.GroupIndex}, ${pColumn.ColumnIndex}, this.checked)">` +
			`<span>${tmpName}</span>` +
			`</label>`;
		const tmpEyeIcon = pPDFHidden ? 'fa-eye-slash' : 'fa-eye';
		const tmpStateClass = pPDFHidden ? ' is-pdf-hidden' : '';
		const tmpDisabledAttr = pHTMLHidden ? ' disabled="disabled"' : '';
		const tmpTitle = pHTMLHidden ?
			'Hidden in all views (including PDF)' :
			(pExplicitPDFHidden ? 'Hidden in PDF — click to show in PDF' : 'Shown in PDF — click to hide in PDF');
		// Toggle target: flip the EXPLICIT PDF-only membership (cascade is read-only).
		const tmpNewHidden = String(!pExplicitPDFHidden);
		const tmpButton = `<button type="button" class="colchooser-pdf-toggle${tmpStateClass}"${tmpDisabledAttr} ` +
			`title="${tmpTitle}" aria-label="${tmpTitle}" ` +
			`onclick="_Pict.providers['Bulma-TableControls'].toggleTabularColumnPDFVisibility('${pView.Hash}', ${pGroup.GroupIndex}, ${pColumn.ColumnIndex}, ${tmpNewHidden})">` +
			`<span class="icon is-small"><i class="fas fa-file-pdf" aria-hidden="true"></i></span>` +
			`<span class="icon is-small"><i class="fas ${tmpEyeIcon}" aria-hidden="true"></i></span>` +
			`</button>`;
		return `<div class="pict-tabular-colchooser-row has-pdf-toggle">${tmpCheckbox}${tmpButton}</div>`;
	}

	/**
	 * Inline-handler entry point for the PDF-hide toggle: add/remove a column from
	 * the group's PDF-only hidden set, persist it to form data, and repaint the
	 * popover (the interactive table is not rebuilt — its column set is unchanged).
	 * Refuses to hide the last column that would still print.
	 * @param {string} pViewHash - The view hash.
	 * @param {number|string} pGroupIndex - The tabular group index.
	 * @param {number|string} pColumnIndex - The column's manifest index.
	 * @param {boolean} pHidden - true to hide the column in PDF, false to show it.
	 * @return {boolean} - True on success.
	 */
	toggleTabularColumnPDFVisibility(pViewHash, pGroupIndex, pColumnIndex, pHidden)
	{
		const tmpView = this.pict.views[pViewHash];
		if (!tmpView || !tmpView.sectionDefinition || !Array.isArray(tmpView.sectionDefinition.Groups))
		{
			return false;
		}
		const tmpGroup = tmpView.sectionDefinition.Groups[Number(pGroupIndex)];
		if (!tmpGroup || !(tmpGroup.ColumnChooser && tmpGroup.ColumnChooser.SelectPDFOnlyColumns))
		{
			return false;
		}
		const tmpLayout = this.pict.providers['Pict-Layout-Tabular'];
		if (!tmpLayout || (typeof tmpLayout._getTabularChoosableColumns !== 'function'))
		{
			return false;
		}
		const tmpColumns = tmpLayout._getTabularChoosableColumns(tmpView, tmpGroup);
		const tmpColumn = tmpColumns.find((pCandidate) => pCandidate.ColumnIndex === Number(pColumnIndex));
		if (!tmpColumn)
		{
			return false;
		}
		const tmpPDFSet = this.getPDFHiddenColumnsSet(tmpView, tmpGroup);
		if (pHidden)
		{
			// Don't let the last column that still prints be hidden — keep a non-empty PDF table.
			const tmpVisibleInPDF = tmpColumns.filter((pCandidate) => pCandidate.Visible && !tmpPDFSet.has(pCandidate.Key)).length;
			if (tmpVisibleInPDF <= 1 && tmpColumn.Visible && !tmpPDFSet.has(tmpColumn.Key))
			{
				this.pict.log.warn(`Headlight PDF column chooser on group [${tmpGroup.Hash}] refused to hide the last PDF-visible column [${tmpColumn.Key}].`);
				this.rerenderColumnChooserPopover(tmpView, tmpGroup);
				return false;
			}
			tmpPDFSet.add(tmpColumn.Key);
		}
		else
		{
			tmpPDFSet.delete(tmpColumn.Key);
		}
		this.pict.setStateValueAtAddress(this.getPDFHiddenColumnsAddress(tmpView, tmpGroup), null, Array.from(tmpPDFSet));
		this.rerenderColumnChooserPopover(tmpView, tmpGroup);
		return true;
	}

	/**
	 * Repaint a group's chooser popover and keep it open (mirrors what the framework
	 * toggle handlers do after a change).
	 * @param {Object} pView - The form view.
	 * @param {Object} pGroup - The tabular group descriptor.
	 */
	rerenderColumnChooserPopover(pView, pGroup)
	{
		const tmpLayout = this.pict.providers['Pict-Layout-Tabular'];
		if (!tmpLayout || (typeof tmpLayout._renderTabularColumnChooserPopover !== 'function'))
		{
			return;
		}
		tmpLayout._renderTabularColumnChooserPopover(pView, pGroup);
		if (typeof tmpLayout._paintTabularColumnChooserOpenState === 'function')
		{
			tmpLayout._paintTabularColumnChooserOpenState(pView, pGroup, true);
		}
	}

	/**
	 * Render the left-position editing-controls cell: the same menu button the
	 * right-side ('Postfix') controls use. Mirrors -TabularTemplate-Row-ExtraPostfix.
	 * @param {Object} pView - The form view.
	 * @param {number|string} pGroupIndex - The tabular group index.
	 * @param {number|string} pRowKey - The row key/index.
	 * @return {string} - The <td> editing-controls cell HTML.
	 */
	renderTabularEditingControlsMenuCell(pView, pGroupIndex, pRowKey)
	{
		if (!pView)
		{
			return '';
		}
		const tmpUUID = pView.UUID;
		const tmpViewHash = pView.Hash;
		const tmpButtonID = `TABLE-MENU-BUTTON-FOR-${tmpUUID}-${tmpViewHash}-${pGroupIndex}-${pRowKey}`;
		return `<td class="extra-table-column" data-table-controls="menu">` +
			`<button id="${tmpButtonID}" class="button is-link is-outlined" type="button" aria-haspopup="true"` +
			` onmouseover="_Pict.providers['Bulma-TableControls'].showMenuForTable(id, '${tmpUUID}', '${tmpViewHash}', '${pGroupIndex}', '${pRowKey}', event)"` +
			` onclick="_Pict.providers['Bulma-TableControls'].showMenuForTable(id, '${tmpUUID}', '${tmpViewHash}', '${pGroupIndex}', '${pRowKey}', event)">` +
			`<span class="icon is-small"><i class="fas fa-ellipsis" aria-hidden="true"></i></span>` +
			`</button>` +
			`</td>`;
	}

	/**
	 * Generates the HTML ID for a content display input element.
	 *
	 * @param {string} pInputHTMLID - The HTML ID of the input element.
	 * @return {string} - The generated HTML ID for the content display input element.
	 */
	getTableHTMLID(pInputHTMLID)
	{
		return `#TABLE-FOR-${pInputHTMLID}`;
	}
	getTableWrapperHTMLID(pInputHTMLID)
	{
		return `#TABLE-WRAPPER-FOR-${pInputHTMLID}`;
	}
	getTableSliderHTMLID(pInputHTMLID)
	{
		return `#TABLE-SLIDER-FOR-${pInputHTMLID}`;
	}
	getTableSliderWrapperHTMLID(pInputHTMLID)
	{
		return `#TABLE-SLIDER-WRAPPER-FOR-${pInputHTMLID}`;
	}

	/**
	 * Check if an element has scroll overflow.
	 * @param {string} pInputHTMLID - The HTML ID of the element.
	 * @return {boolean} - True if the element has scroll overflow, false otherwise.
	 */
	doesElementHaveScrollOverflow(pInputHTMLID)
	{
		const tmpTableElement = document.querySelector(this.getTableHTMLID(pInputHTMLID));
		const tmpTableWrapperElement = document.querySelector(this.getTableWrapperHTMLID(pInputHTMLID));
		return tmpTableElement.scrollWidth > tmpTableWrapperElement.clientWidth ? true : false;
	}

	/**
	 * Tombstone the rows the row's selectors own before the row is spliced away.
	 *
	 * Row deletion is the one removal route with no input-level seam: `deleteDynamicTableRow` splices
	 * the record set and notifies nothing, so each selector in the row is asked for its owned rows here,
	 * the last point at which they still exist.
	 *
	 * @param {Object} pView - The dynamic-form view owning the row.
	 * @param {Record<string, any>} pGroup - The group descriptor (for its RecordSetAddress).
	 * @param {number|string} pRowIndex - The row about to be deleted.
	 * @return {number} - How many tombstones were staged.
	 */
	stageRowRemovalTombstones(pView, pGroup, pRowIndex)
	{
		if (!ScopedEntityData.isEnabled(this.pict))
		{
			return 0;
		}
		const tmpRemovals = ScopedEntityData.collectRowSelectorRemovals(this.pict, pView, pGroup, pRowIndex);
		if (tmpRemovals.length < 1)
		{
			return 0;
		}
		const tmpResult = ScopedEntityData.tombstoneRemovedRecords(this.pict, ScopedEntityData.DEFAULT_CONTEXT, tmpRemovals);
		if (tmpResult.Staged.length > 0)
		{
			this.pict.log.info(`Bulma-TableControls: staged deletion of ${tmpResult.Staged.length} record(s) with row ${pRowIndex} of [${pGroup.RecordSetAddress}].`, { Records: tmpResult.Staged });
		}
		return tmpResult.Staged.length;
	}

	/**
	 * Handle a table row action from a dropdown menu.
	 * @param {string} pAction - The action to perform.
	 * @param {string} pGUID - The GUID of the table row.
	 * @param {string} pViewHash - The hash of the view.
	 * @param {number} pGroupIndex - The group number of the table row.
	 * @param {string} pRowIndex - The key of the table row.
	 * @return {boolean} - True if the action was successful, false otherwise.
	 */
	handleTableMenuButtonEvent(pAction, pGUID, pViewHash, pGroupIndex, pRowIndex)
	{
		const tmpView = this.pict.views[pViewHash];
		const tmpGroup = tmpView.getGroup(pGroupIndex);
		const tmpDestinationObject = tmpView.sectionManifest.getValueByHash(tmpView.getMarshalDestinationObject(), tmpGroup.RecordSetAddress);
		// Hide the popover menu before the action is performed to avoid visual glitches
		this.fable.HeadlightPopover.hideAll();
		// Perform the action after a short delay to allow the popover to hide properly and not UI jump to the top left of the screen
		setTimeout(() =>
		{
			// handle toast notifications for table row actions based on min/max row counts
			if (pAction === 'Delete')
			{
				if (tmpGroup.MinimumRowCount && (tmpDestinationObject.length <= tmpGroup.MinimumRowCount))
				{
					this.pict.log.error(`Cannot delete row, minimum row count reached: ${tmpGroup.MinimumRowCount}`);
					this.pict.views.HeadlightPanels.toast({
						message: `Cannot delete row, minimum row count reached: ${tmpGroup.MinimumRowCount}`,
						type: 'is-warning',
					});
					this.fable.HeadlightPopover.hideAll();
					return false;
				}
				this.stageRowRemovalTombstones(tmpView, tmpGroup, pRowIndex);
				this.pict.views[pViewHash].deleteDynamicTableRow(pGroupIndex, pRowIndex);
			}
			else if (pAction === 'MoveUp')
			{
				const tmpRowIndex = parseInt(String(pRowIndex), 10);
				if (tmpRowIndex == 0)
				{
					this.pict.log.error(`Cannot move row up, already at the top: ${tmpRowIndex}`);
					this.pict.views.HeadlightPanels.toast({
						message: `Cannot move row up, already at the top.`,
						type: 'is-warning',
					});
					this.fable.HeadlightPopover.hideAll();
					return false;
				}
				this.pict.views[pViewHash].moveDynamicTableRowUp(pGroupIndex, pRowIndex);
			}
			else if (pAction === 'MoveDown')
			{
				const tmpRowIndex = parseInt(String(pRowIndex), 10);
				if (tmpRowIndex == (tmpDestinationObject.length - 1))
				{
					this.pict.log.error(`Cannot move row down, already at the bottom: ${tmpRowIndex}`);
					this.pict.views.HeadlightPanels.toast({
						message: `Cannot move row down, already at the bottom.`,
						type: 'is-warning',
					});
					this.fable.HeadlightPopover.hideAll();
					return false;
				}
				this.pict.views[pViewHash].moveDynamicTableRowDown(pGroupIndex, pRowIndex);
			}
			else
			{
				this.pict.log.error(`Unknown action: ${pAction}`);
			}
			// Update any necessary controls
			this.prepareAnyExtraTableControls();
		}, 0);
		return true;
	}

	/**
	 * Add a dropdown menu to the toggle button for a table row.
	 * @param {string} pElementId - The ID of the element to attach the popover menu to.
	 * @param {string} pGUID - The GUID of the table row.
	 * @param {string} pViewHash - The hash of the view.
	 * @param {number} pGroup - The group number of the table row.
	 * @param {string} pKey - The key of the table row.
	 * @param {Event} pEvent - The event that triggered the function.
	 */
	showMenuForTable(pElementId, pGUID, pViewHash, pGroup, pKey, pEvent)
	{
		const tmpMenuElement = document.getElementById(`TABLE-MENU-BUTTON-FOR-${pGUID}-${pViewHash}-${pGroup}-${pKey}`);
		if (!tmpMenuElement)
		{
			return;
		}

		this.fable.HeadlightPopover.createPopover({
			PopoverDestinationAddress: pElementId,
			PopoverTemplateHash: 'PictProvider-Theme-Edit-TabularTemplate-RowHeader-ExtraPostfix-TableMenu',
			PopoverTemplateRecord:
				{
					UUID: pGUID,
					Hash: pViewHash,
					Group: pGroup,
					Key: pKey,
				},
			theme: 'dropdown',
			delay: [ 500, 0 ], // delay showing the menu to allow for click events to propagate, avoid accidental hover displays, etc.
		});
	}

	/**
	 * Show any slider controls for tables that have scroll overflow.
	 */
	prepareAnyExtraTableControls()
	{
		const tmpScrollTables = document.querySelectorAll('.section-table-wrapper table');

		this.pict.log.info(`Checking ${ tmpScrollTables.length } tables for scroll overflow...`);
		tmpScrollTables.forEach((pTable) =>
		{
			if (!pTable.id.startsWith('TABLE-FOR-'))
			{
				return;
			}
			const tmpHTMLID = pTable.id.replace('TABLE-FOR-', '');
			// add a slider control to the table if it has scroll overflow
			this.toggleScrollTableSlider(tmpHTMLID);
			// Enable/disable both add bars (the default bottom one and the opt-in top
			// one) against MaximumRowCount.
			//
			// NOTE the placeholder row's class really is 'table-emtpy-row' (sic) -- that
			// is what the group templates emit. This used to count every tbody row and
			// then subtract one via `tr:not(.table-empty-row)`, a selector whose spelling
			// matched nothing, so it happened to land on the right number only because
			// there is always exactly one placeholder. Exclude it directly instead.
			const currentNumRows = pTable.querySelectorAll('tbody tr:not(.table-emtpy-row)').length;
			this.applyAddButtonRowLimit(
				[ document.getElementById(`TABLE-ADD-BUTTON-FOR-${tmpHTMLID}`), document.getElementById(`TABLE-ADD-BUTTON-TOP-FOR-${tmpHTMLID}`) ],
				currentNumRows, 'data-max-rows', 'data-min-rows');
		});
		// reveal the inline/modal entry toggle on groups that have grown large enough
		this.refreshEntryModeToggleVisibility();
		// recalculate responsive UI adjustments for label alignments, etc.
		this.pict.providers.ResponsiveUIAdjustmentProvider.onWindowResize();
	}

	/**
	 * Toggle the slider control for a table that has scroll overflow.
	 * @param {string} pHTMLSelector - The HTML ID of the table.
	 */
	toggleScrollTableSlider(pHTMLSelector)
	{
		const tmpSliderElement = document.querySelector(this.getTableSliderWrapperHTMLID(pHTMLSelector));
		if (tmpSliderElement)
		{
			if (this.doesElementHaveScrollOverflow(pHTMLSelector))
			{
				this.pict.log.info(`Adding slider to overflow table ID: ${ pHTMLSelector }`);
				tmpSliderElement.classList.remove('is-hidden');
			}
			else
			{
				tmpSliderElement.classList.add('is-hidden');
			}
		}
	}

	/**
	 * Handle the click event for the "Toggle Extra Column" button in a table.
	 * @param {Event} event - The click event.
	 * @param {string} pInputHTMLID - The HTML ID of the table.
	 */
	handleToggleExtraColumnButtonClickEvents(event, pInputHTMLID)
	{
		const tmpButton = /** @type {HTMLButtonElement} */ (event.currentTarget);
		const tmpTableWrapperElement = document.querySelector(this.getTableWrapperHTMLID(pInputHTMLID));
		if (tmpTableWrapperElement && tmpButton)
		{
			// find the parent '.section-table-wrapper' element and toggle the 'hide-extra-table-columns' class on it to show/hide the extra column via CSS
			const tmpSectionTableWrapperElement = tmpTableWrapperElement.closest('.section-table-wrapper');
			if (tmpSectionTableWrapperElement)
			{
				const isVisible = !tmpSectionTableWrapperElement.classList.contains('hide-extra-table-columns');
				if (isVisible)
				{
					tmpSectionTableWrapperElement.classList.add('hide-extra-table-columns');
					this.setMenuColumnToggleButtonContent(tmpButton, 'fa-eye', ' Show Menu Column');
				}
				else
				{
					tmpSectionTableWrapperElement.classList.remove('hide-extra-table-columns');
					this.setMenuColumnToggleButtonContent(tmpButton, 'fa-eye-slash', ' Hide Menu Column');
				}
				// recalculate responsive UI adjustments for label alignments, etc.
				this.pict.providers.ResponsiveUIAdjustmentProvider.onWindowResize();
			}
		}
	}

	/**
	 * Repopulate the show/hide menu-column toggle button with an icon + label via DOM
	 * nodes (not an HTML string).
	 * @param {HTMLButtonElement} pButton - The toggle button to repopulate.
	 * @param {string} pIconClass - The Font Awesome icon class (e.g. 'fa-eye').
	 * @param {string} pLabel - The button label (leading space preserved for icon spacing).
	 */
	setMenuColumnToggleButtonContent(pButton, pIconClass, pLabel)
	{
		const tmpIconSpan = document.createElement('span');
		tmpIconSpan.className = 'icon';
		const tmpIcon = document.createElement('i');
		tmpIcon.className = `fas ${pIconClass}`;
		tmpIcon.setAttribute('aria-hidden', 'true'); // decorative; label text carries meaning
		tmpIconSpan.appendChild(tmpIcon);

		const tmpLabelSpan = document.createElement('span');
		tmpLabelSpan.textContent = pLabel;

		// Clear via removeChild loop (replaceChildren isn't in this bundle's browserslist target).
		while (pButton.firstChild)
		{
			pButton.removeChild(pButton.firstChild);
		}
		pButton.appendChild(tmpIconSpan);
		pButton.appendChild(tmpLabelSpan);
	}

	/**
	 * Handle the scroll event for a table that has scroll overflow the user uses the slider control to scroll.
	 * @param {string} pInputHTMLID - The HTML ID of the table.
	 * @param {Event} pEvent - The event that triggered the function.
	 */
	updateScrollTableFromSliderEvent(pInputHTMLID, pEvent)
	{
		pEvent.preventDefault();
		pEvent.stopPropagation();
		//console.log('handleScrollTableBySliderEvent', pEvent.currentTarget.value);
		// make the slider and the table horizontally scroll together
		/** @type {HTMLDivElement} */
		const tmpTableWrapper = document.querySelector(this.getTableWrapperHTMLID(pInputHTMLID));
		/** @type {HTMLTableElement} */
		const tmpTable = document.querySelector(this.getTableHTMLID(pInputHTMLID));
		/** @type {HTMLInputElement} */
		const tmpSlider = document.querySelector(this.getTableSliderHTMLID(pInputHTMLID));
		// @ts-ignore
		const sliderValue = Number(pEvent.currentTarget.value);
		// if the table and the slider are found, make them scroll together
		if (tmpTableWrapper && tmpTable && tmpSlider)
		{
			// scroll the table horizontally based on the slider value
			const maxScrollLeft = tmpTable.scrollWidth - tmpTableWrapper.clientWidth;
			const scrollLeft = (Number(sliderValue) / 100) * maxScrollLeft;
			tmpTableWrapper.scrollLeft = scrollLeft;
		}
	}

	/**
	 * Handle the scroll event for a table that has scroll overflow the user scrolls the table horizontally.
	 * @param {string} pInputHTMLID - The HTML ID of the table.
	 * @param {Event} pEvent - The event that triggered the function.
	 */
	updateSliderFromScrollTableEvent(pInputHTMLID, pEvent)
	{
		pEvent.preventDefault();
		pEvent.stopPropagation();
		//console.log('updateSliderFromScrollTableEvent', pEvent.currentTarget.scrollLeft);
		// make the slider and the table horizontally scroll together
		/** @type {HTMLDivElement} */
		const tmpTableWrapper = document.querySelector(this.getTableWrapperHTMLID(pInputHTMLID));
		/** @type {HTMLTableElement} */
		const tmpTable = document.querySelector(this.getTableHTMLID(pInputHTMLID));
		/** @type {HTMLInputElement} */
		const tmpSlider = document.querySelector(this.getTableSliderHTMLID(pInputHTMLID));
		// if the table and the slider are found, make them scroll together
		if (tmpTableWrapper && tmpTable && tmpSlider)
		{
			// update the slider value based on the table scroll position
			const maxScrollLeft = tmpTable.scrollWidth - tmpTableWrapper.clientWidth;
			const sliderValue = ((tmpTableWrapper.scrollLeft / maxScrollLeft) * 100).toFixed(0);
			tmpSlider.value = sliderValue;
		}
	}

	/**
	 * Keep the "Add Row" button in view after adding a row to a table.
	 * @param {HTMLButtonElement} pButtonElement - The button element to keep in view.
	 */
	keepButtonInView(pButtonElement)
	{
		// Ensure the button is in view after adding a row
		const tmpButton = pButtonElement;
		const navBottomHeight = document.getElementById('HeadlightPanels-NavBar') ? document.getElementById('HeadlightPanels-NavBar').offsetHeight : 60;
		const extraSpacingGap = 10;
		window.scrollTo({
			top: tmpButton.getBoundingClientRect().top + window.scrollY + navBottomHeight + extraSpacingGap,
			behavior: 'smooth',
		});
	}

	/**
	 * Handle the click event for the "Add Row" button in a table.
	 * NOTE: This is a secondary call to prepare any extra table controls after the row is added/rendered - it doesn't actually add the row (that is handled by the Pict Provider).
	 * @param {Event} event - The click event.
	 */
	handleAddRowButtonClickEvents(event)
	{
		/** @ts-ignore */
		const tmpButton = event.currentTarget.TagName === 'BUTTON' ? event.currentTarget : event.currentTarget.parentElement;
		// After a short delay, keep the button in view
		setTimeout(() =>
		{
			this.keepButtonInView(tmpButton);
			// Update any necessary controls
			this.prepareAnyExtraTableControls();
		}, 100);
	}

	// ═════════════════════════════════════════════════════════════════════
	// Empty-table message
	// ═════════════════════════════════════════════════════════════════════

	/**
	 * Resolve the copy for a tabular group's "no rows" placeholder row.
	 *
	 * Backs the `{~TabularEmptyMessage:<address>:<default copy>~}` macro. The
	 * pattern body is split on the FIRST colon so the default copy may itself
	 * contain colons; everything after that first colon is the fallback.
	 *
	 * @param {string} pPatternBody - The macro body: `<address>:<default copy>`.
	 * @param {Object} pData - The template data scope.
	 * @param {any} [fCallback] - Passed through to resolveStateFromAddress.
	 * @return {string} - The escaped message text.
	 */
	resolveTabularEmptyMessage(pPatternBody, pData, fCallback)
	{
		const tmpBody = (typeof pPatternBody === 'string') ? pPatternBody : '';
		const tmpSeparatorIndex = tmpBody.indexOf(':');
		const tmpAddress = (tmpSeparatorIndex < 0) ? tmpBody : tmpBody.substring(0, tmpSeparatorIndex);
		const tmpDefaultMessage = (tmpSeparatorIndex < 0) ? '' : tmpBody.substring(tmpSeparatorIndex + 1);
		const tmpGroup = this.pict.resolveStateFromAddress(tmpAddress, pData, fCallback);
		// A group that never opted in falls back to the theme's own literal, which
		// the template passes in -- that is what keeps every existing form identical.
		const tmpMessage = (tmpGroup && tmpGroup.EmptyMessage) ? tmpGroup.EmptyMessage : tmpDefaultMessage;
		return _escapeTemplateText(tmpMessage);
	}

	// ═════════════════════════════════════════════════════════════════════
	// Entry controls
	// ═════════════════════════════════════════════════════════════════════

	/**
	 * Normalize a group's `EntryControls` block. Returns null when the group did
	 * not opt in (or explicitly disabled it) -- every caller treats null as
	 * "behave exactly the way this table behaved before the feature existed".
	 * @param {Object} pGroup - The group descriptor.
	 * @return {Object|null} - The normalized config, or null.
	 */
	getEntryControlsConfig(pGroup)
	{
		if (!pGroup || !pGroup.EntryControls || (typeof pGroup.EntryControls !== 'object'))
		{
			return null;
		}
		const tmpRaw = pGroup.EntryControls;
		if (tmpRaw.Enabled === false)
		{
			return null;
		}
		const tmpAutoReveal = (tmpRaw.ModeToggleAutoReveal && (typeof tmpRaw.ModeToggleAutoReveal === 'object')) ? tmpRaw.ModeToggleAutoReveal : {};
		const tmpPosition = ([ 'top', 'bottom', 'both' ].indexOf(tmpRaw.Position) > -1) ? tmpRaw.Position : _EntryControlsDefaults.Position;
		const tmpEntryPoint = ([ 'start', 'end' ].indexOf(tmpRaw.EntryPoint) > -1) ? tmpRaw.EntryPoint : _EntryControlsDefaults.EntryPoint;
		const tmpMode = ([ 'inline', 'modal' ].indexOf(tmpRaw.Mode) > -1) ? tmpRaw.Mode : _EntryControlsDefaults.Mode;
		return (
			{
				Position: tmpPosition,
				EntryPoint: tmpEntryPoint,
				Mode: tmpMode,
				AllowModeToggle: (tmpRaw.AllowModeToggle === true),
				RowThreshold: (typeof tmpAutoReveal.RowThreshold === 'number') ? tmpAutoReveal.RowThreshold : _EntryControlsDefaults.RowThreshold,
				// A ViewportMultiple of 0 disables the height half of the heuristic.
				ViewportMultiple: (typeof tmpAutoReveal.ViewportMultiple === 'number') ? tmpAutoReveal.ViewportMultiple : _EntryControlsDefaults.ViewportMultiple,
				// `false` means "always visible" -- skip the size heuristic entirely.
				AutoReveal: (tmpRaw.ModeToggleAutoReveal !== false),
				ModalTitle: (typeof tmpRaw.ModalTitle === 'string') ? tmpRaw.ModalTitle : _EntryControlsDefaults.ModalTitle,
				ModalWidth: (typeof tmpRaw.ModalWidth === 'string') ? tmpRaw.ModalWidth : _EntryControlsDefaults.ModalWidth,
				ModalColumns: (tmpRaw.ModalColumns === 2) ? 2 : _EntryControlsDefaults.ModalColumns,
			});
	}

	/**
	 * True when the group renders through the RecordSet layout rather than Tabular.
	 * @param {Object} pGroup - The group descriptor.
	 * @return {boolean}
	 */
	isRecordSetGroup(pGroup)
	{
		return !!(pGroup && (pGroup.Layout === 'RecordSet'));
	}

	/**
	 * The form view hash that owns a group.
	 *
	 * Groups are not stamped with it, so find the section view whose own group list
	 * CONTAINS this exact group object. Do NOT read PictForm.ViewHash off the
	 * supportingManifest descriptors: ManifestFactory writes that onto the shared
	 * `ReferenceManifests` descriptor objects, so two groups built from the same
	 * RecordManifest overwrite each other and the last view to initialize wins.
	 *
	 * @param {Object} pGroup - The group descriptor.
	 * @return {string} - The view hash, or '' when it cannot be resolved.
	 */
	getGroupViewHash(pGroup)
	{
		if (!pGroup)
		{
			return '';
		}
		if (typeof pGroup._EntryControlsViewHash === 'string')
		{
			return pGroup._EntryControlsViewHash;
		}
		const tmpViewHashes = Object.keys(this.pict.views || {});
		for (let i = 0; i < tmpViewHashes.length; i++)
		{
			const tmpView = this.pict.views[tmpViewHashes[i]];
			if (!tmpView || !tmpView.isPictSectionForm || (typeof tmpView.getGroups !== 'function'))
			{
				continue;
			}
			const tmpGroups = tmpView.getGroups();
			if (!Array.isArray(tmpGroups))
			{
				continue;
			}
			for (let g = 0; g < tmpGroups.length; g++)
			{
				if (tmpGroups[g] === pGroup)
				{
					pGroup._EntryControlsViewHash = String(tmpView.Hash);
					return pGroup._EntryControlsViewHash;
				}
			}
		}
		return '';
	}

	/**
	 * The localStorage-shaped object the entry-mode preference lives in, or null
	 * when storage is unavailable. Mirrors PictProvider-SpecScopeResolver's
	 * accessor so tests can inject a fake via `options.EntryModeStorage`.
	 * @return {Object|null}
	 */
	_entryModeStorage()
	{
		try
		{
			if (this.options && this.options.EntryModeStorage)
			{
				return this.options.EntryModeStorage;
			}
			return (typeof localStorage !== 'undefined') ? localStorage : null;
		}
		catch (pError)
		{
			return null;
		}
	}

	/**
	 * @param {string} pViewHash - The form view hash.
	 * @param {Object} pGroup - The group descriptor.
	 * @return {string} - The storage key for this group's entry-mode preference.
	 */
	getEntryModeStorageKey(pViewHash, pGroup)
	{
		return `${_ENTRY_MODE_STORAGE_PREFIX}.${pViewHash}.${(pGroup && pGroup.Hash) || 'UnknownGroup'}`;
	}

	/**
	 * The user's stored inline/modal preference for a group, or null when they
	 * have never chosen. Never read when the group didn't allow the toggle.
	 * @param {string} pViewHash - The form view hash.
	 * @param {Object} pGroup - The group descriptor.
	 * @param {Object} pConfig - The normalized EntryControls config.
	 * @return {string|null} - 'inline' | 'modal' | null.
	 */
	readEntryModePreference(pViewHash, pGroup, pConfig)
	{
		if (!pConfig || !pConfig.AllowModeToggle)
		{
			return null;
		}
		const tmpStorage = this._entryModeStorage();
		if (!tmpStorage)
		{
			return null;
		}
		try
		{
			const tmpValue = tmpStorage.getItem(this.getEntryModeStorageKey(pViewHash, pGroup));
			return ((tmpValue === 'inline') || (tmpValue === 'modal')) ? tmpValue : null;
		}
		catch (pError)
		{
			return null;
		}
	}

	/**
	 * Persist the user's inline/modal preference for a group.
	 * @param {string} pViewHash - The form view hash.
	 * @param {Object} pGroup - The group descriptor.
	 * @param {Object} pConfig - The normalized EntryControls config.
	 * @param {string} pMode - 'inline' | 'modal'.
	 * @return {boolean} - True when it was written.
	 */
	writeEntryModePreference(pViewHash, pGroup, pConfig, pMode)
	{
		if (!pConfig || !pConfig.AllowModeToggle || ((pMode !== 'inline') && (pMode !== 'modal')))
		{
			return false;
		}
		const tmpStorage = this._entryModeStorage();
		if (!tmpStorage)
		{
			return false;
		}
		try
		{
			tmpStorage.setItem(this.getEntryModeStorageKey(pViewHash, pGroup), pMode);
			return true;
		}
		catch (pError)
		{
			return false;
		}
	}

	/**
	 * The mode that actually applies right now: the group's configured default,
	 * overridden by the user's stored preference when the toggle is allowed.
	 * @param {string} pViewHash - The form view hash.
	 * @param {Object} pGroup - The group descriptor.
	 * @return {string} - 'inline' | 'modal'.
	 */
	getEffectiveEntryMode(pViewHash, pGroup)
	{
		const tmpConfig = this.getEntryControlsConfig(pGroup);
		if (!tmpConfig)
		{
			return 'inline';
		}
		return this.readEntryModePreference(pViewHash, pGroup, tmpConfig) || tmpConfig.Mode;
	}

	/**
	 * The opt-in TOP add bar for a group, or '' when the group did not ask for
	 * one. Emitted as a whole from a macro (rather than living in the template
	 * behind a hide class) so that a group without `EntryControls` produces no
	 * extra DOM at all -- existing forms render exactly what they rendered before.
	 * @param {Object} pGroup - The group descriptor.
	 * @return {string} - The bar HTML.
	 */
	renderEntryAddBarHTML(pGroup)
	{
		const tmpConfig = this.getEntryControlsConfig(pGroup);
		if (!tmpConfig || ((tmpConfig.Position !== 'top') && (tmpConfig.Position !== 'both')))
		{
			return '';
		}
		const tmpViewHash = this.getGroupViewHash(pGroup);
		const tmpView = this.pict.views[tmpViewHash];
		if (!tmpView)
		{
			this.pict.log.warn(`Entry controls on group [${pGroup.Hash}] could not resolve its form view; skipping the top add bar.`);
			return '';
		}
		const tmpIsRecordSet = this.isRecordSetGroup(pGroup);
		const tmpProviderHash = tmpIsRecordSet ? 'Bulma-RecordSetControls' : 'Bulma-TableControls';
		// The top bar is tagged 'entry-bar-top' so it is still swept up by the
		// `.hide-table-controls [data-table-controls]` /
		// `.hide-recordset-controls [data-recordset-controls]` suppression rules.
		const tmpControlsAttribute = tmpIsRecordSet ? 'data-recordset-controls' : 'data-table-controls';
		const tmpElementKey = `${tmpView.UUID}-${tmpViewHash}-${pGroup.Hash}`;
		const tmpButtonID = `${tmpIsRecordSet ? 'RECORDSET' : 'TABLE'}-ADD-BUTTON-TOP-FOR-${tmpElementKey}`;
		const tmpLabel = tmpIsRecordSet ? 'Add new set' : 'Add new row';
		const tmpMaxAttribute = tmpIsRecordSet ? 'data-max-sets' : 'data-max-rows';
		const tmpMinAttribute = tmpIsRecordSet ? 'data-min-sets' : 'data-min-rows';
		const tmpMaxValue = (pGroup.MaximumRowCount == null) ? '' : String(pGroup.MaximumRowCount);
		const tmpMinValue = (pGroup.MinimumRowCount == null) ? '' : String(pGroup.MinimumRowCount);
		return `<div class="columns is-vcentered is-mobile p-0 m-0" ${tmpControlsAttribute}="entry-bar-top">` +
			`<div class="column p-0 mt-2 mb-2 mr-3 has-text-right">` +
			this.renderEntryModeToggleHTML(pGroup) +
			`<button type="button" id="${this._escapeEntryAttribute(tmpButtonID)}" class="button is-link"` +
			` ${tmpControlsAttribute}="${tmpIsRecordSet ? 'add-set' : 'add-row'}"` +
			` ${tmpMaxAttribute}="${this._escapeEntryAttribute(tmpMaxValue)}" ${tmpMinAttribute}="${this._escapeEntryAttribute(tmpMinValue)}"` +
			` onclick="_Pict.providers['${tmpProviderHash}'].handleAddEntryButtonClickEvents(event, '${this._escapeEntryAttribute(tmpViewHash)}', '${this._escapeEntryAttribute(String(pGroup.GroupIndex))}')">` +
			`<span class="icon"><i class="fas fa-plus" aria-hidden="true"></i></span>` +
			`<span> ${tmpLabel}</span>` +
			`</button>` +
			`</div>` +
			`</div>`;
	}

	/**
	 * The inline/modal toggle button markup, or '' when the group didn't opt in.
	 * Starts hidden; refreshEntryModeToggleVisibility() reveals it.
	 * @param {Object} pGroup - The group descriptor.
	 * @return {string} - The button HTML.
	 */
	renderEntryModeToggleHTML(pGroup)
	{
		const tmpConfig = this.getEntryControlsConfig(pGroup);
		if (!tmpConfig || !tmpConfig.AllowModeToggle)
		{
			return '';
		}
		const tmpViewHash = this.getGroupViewHash(pGroup);
		const tmpMode = tmpViewHash ? this.getEffectiveEntryMode(tmpViewHash, pGroup) : tmpConfig.Mode;
		const tmpIsModal = (tmpMode === 'modal');
		const tmpLabel = tmpIsModal ? 'Entry: Dialog' : 'Entry: Inline';
		const tmpIcon = tmpIsModal ? 'fa-window-restore' : 'fa-list';
		const tmpTitle = tmpIsModal ?
			'New entries open in a dialog. Click to switch to inline entry.' :
			'New entries are added inline. Click to switch to dialog entry.';
		// Hidden until the group is big enough to warrant the choice (or always,
		// when the author turned the heuristic off).
		const tmpHiddenClass = tmpConfig.AutoReveal ? ' is-hidden' : '';
		const tmpProviderHash = this.isRecordSetGroup(pGroup) ? 'Bulma-RecordSetControls' : 'Bulma-TableControls';
		return `<button type="button" class="button is-link is-outlined pict-entry-mode-toggle${tmpHiddenClass}"` +
			` data-entry-mode-toggle="${this._escapeEntryAttribute(String(pGroup.Hash))}"` +
			` data-entry-mode-group-index="${this._escapeEntryAttribute(String(pGroup.GroupIndex))}"` +
			` data-entry-mode-rows="${tmpConfig.RowThreshold}"` +
			` data-entry-mode-viewport="${tmpConfig.ViewportMultiple}"` +
			` title="${tmpTitle}" aria-label="${tmpTitle}"` +
			` onclick="_Pict.providers['${tmpProviderHash}'].toggleEntryMode('${this._escapeEntryAttribute(tmpViewHash)}', '${this._escapeEntryAttribute(String(pGroup.GroupIndex))}')">` +
			`<span class="icon is-small"><i class="fas ${tmpIcon}" aria-hidden="true"></i></span>` +
			`<span>${tmpLabel}</span>` +
			`</button>`;
	}

	/**
	 * Minimal attribute escaping for values interpolated into the toggle markup.
	 * @param {string} pValue - The raw value.
	 * @return {string} - The escaped value.
	 */
	_escapeEntryAttribute(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
	}

	/**
	 * Flip a group between inline and modal entry, persist the choice, and
	 * repaint the group so the toggle's own label updates.
	 * @param {string} pViewHash - The form view hash.
	 * @param {number|string} pGroupIndex - The group index.
	 * @return {boolean} - True on success.
	 */
	toggleEntryMode(pViewHash, pGroupIndex)
	{
		const tmpView = this.pict.views[pViewHash];
		if (!tmpView)
		{
			return false;
		}
		const tmpGroup = tmpView.getGroup(pGroupIndex);
		const tmpConfig = this.getEntryControlsConfig(tmpGroup);
		if (!tmpConfig || !tmpConfig.AllowModeToggle)
		{
			return false;
		}
		const tmpNextMode = (this.getEffectiveEntryMode(pViewHash, tmpGroup) === 'modal') ? 'inline' : 'modal';
		this.writeEntryModePreference(pViewHash, tmpGroup, tmpConfig, tmpNextMode);
		// The toggle lives in the group's baked markup, so a plain render is
		// enough to relabel it -- no template rebuild needed.
		tmpView.render();
		tmpView.marshalToView();
		this.prepareAnyExtraEntryControls(tmpGroup);
		return true;
	}

	/**
	 * The single entry point for BOTH add bars (top and bottom) on both layouts.
	 * When the group never opted into EntryControls this reproduces the exact
	 * legacy behavior the template's onclick used to inline.
	 * @param {Event} pEvent - The click event.
	 * @param {string} pViewHash - The form view hash.
	 * @param {number|string} pGroupIndex - The group index.
	 * @return {boolean} - True when an entry was started.
	 */
	handleAddEntryButtonClickEvents(pEvent, pViewHash, pGroupIndex)
	{
		const tmpView = this.pict.views[pViewHash];
		if (!tmpView)
		{
			this.pict.log.error(`Entry controls could not resolve view [${pViewHash}].`);
			return false;
		}
		const tmpGroup = tmpView.getGroup(pGroupIndex);
		const tmpConfig = this.getEntryControlsConfig(tmpGroup);

		if (!tmpConfig)
		{
			// Legacy path: create the row, then the original post-add fixups.
			tmpView.createDynamicTableRow(pGroupIndex);
			this.handleAddRowButtonClickEvents(pEvent);
			return true;
		}

		if (this.getEffectiveEntryMode(pViewHash, tmpGroup) === 'modal')
		{
			return this.openEntryModal(pViewHash, pGroupIndex);
		}
		return this.addEntryInline(pViewHash, pGroupIndex, tmpConfig);
	}

	/**
	 * Add a row inline, honoring EntryPoint. For 'start' the row is appended
	 * first and only then moved to index 0 -- see the comment in
	 * moveStagedEntryToStart for why the array must never be spliced at 0
	 * against a stale DOM.
	 * @param {string} pViewHash - The form view hash.
	 * @param {number|string} pGroupIndex - The group index.
	 * @param {Object} pConfig - The normalized EntryControls config.
	 * @return {boolean} - True on success.
	 */
	addEntryInline(pViewHash, pGroupIndex, pConfig)
	{
		const tmpView = this.pict.views[pViewHash];
		const tmpGroup = tmpView.getGroup(pGroupIndex);
		const tmpRecordSet = this.getEntryRecordSet(tmpView, tmpGroup);
		if (!Array.isArray(tmpRecordSet))
		{
			return false;
		}
		const tmpLengthBefore = tmpRecordSet.length;
		tmpView.createDynamicTableRow(pGroupIndex);
		if (tmpRecordSet.length === tmpLengthBefore)
		{
			// createDynamicTableRow warns and no-ops at MaximumRowCount.
			this.toastEntryControlsMessage(`Cannot add: the maximum of ${tmpGroup.MaximumRowCount} has been reached.`);
			return false;
		}
		const tmpIndex = this.moveStagedEntryToStart(tmpView, pGroupIndex, pConfig, tmpRecordSet.length - 1);
		if (tmpIndex !== (tmpRecordSet.length - 1))
		{
			// The row moved, so the table DOM has to be rebuilt -- see the note in
			// moveStagedEntryToStart about undefined values not clearing an input.
			this.reconcileRecordSet(tmpView, tmpGroup);
		}
		this.prepareAnyExtraEntryControls(tmpGroup);
		this.scrollEntryIntoView(pViewHash, pGroupIndex, tmpIndex);
		return true;
	}


	/**
	 * Reflect a group's MaximumRowCount on its add button(s).
	 *
	 * Toggles BOTH ways on purpose. These functions used to only ever set
	 * `disabled`, never clear it, so a group that hit its cap kept a dead add
	 * button even after the user deleted rows back under it. Nothing else in the
	 * bundle disables an add button, so clearing it here is safe.
	 *
	 * @param {NodeList|Array} pButtons - The add buttons for one group (top and/or bottom).
	 * @param {number} pEntryCount - How many entries the group currently has.
	 * @param {string} pMaxAttribute - 'data-max-rows' (Tabular) or 'data-max-sets' (RecordSet).
	 * @param {string} pMinAttribute - 'data-min-rows' or 'data-min-sets'.
	 * @return {void}
	 */
	applyAddButtonRowLimit(pButtons, pEntryCount, pMaxAttribute, pMinAttribute)
	{
		for (let i = 0; i < pButtons.length; i++)
		{
			const tmpButton = pButtons[i];
			if (!tmpButton)
			{
				continue;
			}
			const tmpMaximum = parseInt(tmpButton.getAttribute(pMaxAttribute), 10);
			const tmpMinimum = parseInt(tmpButton.getAttribute(pMinAttribute), 10) || 0;
			// No configured maximum means the group is never capped.
			if (isNaN(tmpMaximum) || ((tmpMaximum > tmpMinimum) && (pEntryCount < tmpMaximum)))
			{
				tmpButton.removeAttribute('disabled');
				continue;
			}
			tmpButton.setAttribute('disabled', 'disabled');
		}
	}

	/**
	 * Run the post-mutation control fixups appropriate to a group's layout.
	 * @param {Object} pGroup - The group descriptor.
	 * @return {void}
	 */
	prepareAnyExtraEntryControls(pGroup)
	{
		if (this.isRecordSetGroup(pGroup))
		{
			const tmpRecordSetControls = this.pict.providers['Bulma-RecordSetControls'];
			if (tmpRecordSetControls && (typeof tmpRecordSetControls.prepareAnyExtraRecordSetControls === 'function'))
			{
				tmpRecordSetControls.prepareAnyExtraRecordSetControls();
				// The RecordSet path has no table scan of its own, so drive the
				// toggle reveal from here.
				this.refreshEntryModeToggleVisibility();
				return;
			}
		}
		this.prepareAnyExtraTableControls();
	}

	/**
	 * The record-set array backing a group.
	 * @param {Object} pView - The form view.
	 * @param {Object} pGroup - The group descriptor.
	 * @return {Array|null}
	 */
	getEntryRecordSet(pView, pGroup)
	{
		if (!pView || !pGroup || (typeof pGroup.RecordSetAddress !== 'string'))
		{
			return null;
		}
		const tmpRecordSet = pView.sectionManifest.getValueByHash(pView.getMarshalDestinationObject(), pGroup.RecordSetAddress);
		return Array.isArray(tmpRecordSet) ? tmpRecordSet : null;
	}

	/**
	 * Move a just-added entry to the top when EntryPoint is 'start'.
	 *
	 * This MUST go through setDynamicTableRowIndex rather than splicing the array
	 * directly. `data-i-index` is baked into the DOM at render time and an
	 * ordinary marshal does not re-render, so splicing at index 0 against a table
	 * that still shows the old order leaves every existing row's index off by one
	 * and Informary writes row k's DOM values into record k+1. setDynamicTableRowIndex
	 * also permutes dependent positional DynamicColumns before they re-resolve.
	 *
	 * The CALLER must then re-render the group. setDynamicTableRowIndex's repaint
	 * deliberately skips re-rendering the source view (re-initializing entity
	 * selectors costs seconds per move) and relies on the marshal pushing the
	 * reordered model into the existing row DOM. That is fine for a user-initiated
	 * move of populated rows, but a freshly added row's fields are `undefined` and
	 * Informary does not clear an input for an undefined value -- so the new empty
	 * row at index 0 would keep displaying whatever used to be there.
	 *
	 * @param {Object} pView - The form view.
	 * @param {number|string} pGroupIndex - The group index.
	 * @param {Object} pConfig - The normalized EntryControls config.
	 * @param {number} pIndex - The index the new entry currently occupies.
	 * @return {number} - The index the entry ended up at.
	 */
	moveStagedEntryToStart(pView, pGroupIndex, pConfig, pIndex)
	{
		if (!pConfig || (pConfig.EntryPoint !== 'start') || (pIndex <= 0))
		{
			return pIndex;
		}
		pView.setDynamicTableRowIndex(pGroupIndex, pIndex, 0);
		return 0;
	}

	/**
	 * Non-blocking notice through the Headlight panels toast, when available.
	 * @param {string} pMessage - The message.
	 * @param {string} [pType] - A Bulma type class (default 'is-warning').
	 * @return {void}
	 */
	toastEntryControlsMessage(pMessage, pType)
	{
		this.pict.log.warn(pMessage);
		const tmpPanels = this.pict.views ? this.pict.views.HeadlightPanels : null;
		if (tmpPanels && (typeof tmpPanels.toast === 'function'))
		{
			tmpPanels.toast({ message: pMessage, type: pType || 'is-warning' });
		}
	}

	/**
	 * Open the "add a new entry" modal for a group.
	 *
	 * Stage-then-commit: a REAL row is appended to the record set first (without
	 * render/solve/marshal), and the modal renders that row's inputs. Because the
	 * row is genuinely in the model, per-row RecordSetSolvers, ReadOnly computed
	 * fields and AutofillTriggerGroup / EntityBundleRequest cascades all work
	 * live inside the dialog. Because the table is NOT re-rendered while the modal
	 * is open, the staged row has no inline DOM twin -- `data-i-index` for that
	 * index exists in exactly one place in the document.
	 *
	 * @param {string} pViewHash - The form view hash.
	 * @param {number|string} pGroupIndex - The group index.
	 * @return {boolean} - True when the modal opened.
	 */
	openEntryModal(pViewHash, pGroupIndex)
	{
		const tmpView = this.pict.views[pViewHash];
		const tmpGroup = tmpView ? tmpView.getGroup(pGroupIndex) : null;
		const tmpConfig = this.getEntryControlsConfig(tmpGroup);
		if (!tmpView || !tmpGroup || !tmpConfig)
		{
			return false;
		}

		const tmpModal = this.pict.views['Pict-Section-Modal'] || this.pict.views['PictSectionModal'];
		if (!tmpModal || (typeof tmpModal.show !== 'function'))
		{
			// pict-section-modal is a soft dependency -- degrade to inline entry.
			this.pict.log.warn(`Entry controls on group [${tmpGroup.Hash}] requested modal entry but pict-section-modal is unavailable; adding inline.`);
			return this.addEntryInline(pViewHash, pGroupIndex, tmpConfig);
		}
		if (this._modalEntryState)
		{
			this.pict.log.warn('An entry modal is already open; ignoring the second request.');
			return false;
		}

		const tmpRecordSet = this.getEntryRecordSet(tmpView, tmpGroup);
		if (!tmpRecordSet)
		{
			return false;
		}
		if (tmpGroup.MaximumRowCount && (tmpRecordSet.length >= tmpGroup.MaximumRowCount))
		{
			this.toastEntryControlsMessage(`Cannot add: the maximum of ${tmpGroup.MaximumRowCount} has been reached.`);
			return false;
		}

		// Stage the row. Always at the END, even for EntryPoint 'start' -- the
		// move to index 0 happens on commit, after a render has refreshed every
		// row's data-i-index.
		const tmpLengthBefore = tmpRecordSet.length;
		this.pict.providers.DynamicTabularData.createDynamicTableRowWithoutEvents(tmpView, pGroupIndex);
		if (tmpRecordSet.length === tmpLengthBefore)
		{
			this.toastEntryControlsMessage(`Cannot add: the maximum of ${tmpGroup.MaximumRowCount} has been reached.`);
			return false;
		}
		const tmpStagedIndex = tmpRecordSet.length - 1;

		this._modalEntryState =
			{
				ViewHash: pViewHash,
				GroupIndex: Number(pGroupIndex),
				StagedIndex: tmpStagedIndex,
				Committed: false,
				Dialog: null,
			};

		const tmpIsRecordSet = this.isRecordSetGroup(tmpGroup);
		const tmpTemplateHash = tmpIsRecordSet ?
			tmpGroup.SectionTabularRowTemplateHash :
			this.buildModalRowTemplate(tmpView, tmpGroup, tmpConfig);

		// Context[0] must be the view and Context[2] the {Value, Key, Group} row
		// wrapper -- the MetaTemplate parser pushes exactly one level per nesting
		// and parseTemplateByHash pushes the record, so pass TWO context entries.
		// Get this wrong and data-i-index and the onchange handler render blank.
		const tmpRowHTML = this.pict.parseTemplateByHash(
			tmpTemplateHash,
			{ Value: tmpRecordSet[tmpStagedIndex], Key: tmpStagedIndex, Group: Number(pGroupIndex) },
			null,
			[ tmpView, tmpView.sectionDefinition ]);

		const tmpNounLabel = tmpIsRecordSet ? 'Record' : 'Row';
		tmpModal.show(
			{
				title: tmpConfig.ModalTitle || `Add New ${tmpGroup.Name || tmpNounLabel}`,
				content: `<div class="pict-entry-modal-body">${tmpRowHTML}</div>`,
				width: tmpConfig.ModalWidth,
				closeable: true,
				buttons:
					[
						{ Hash: 'cancel', Label: 'Cancel' },
						{ Hash: 'done', Label: 'Done', Style: 'primary' },
						{ Hash: 'done-goto', Label: `Finish and go to ${tmpNounLabel}`, Style: 'primary' },
					],
				onOpen: (pDialog) =>
				{
					if (this._modalEntryState)
					{
						this._modalEntryState.Dialog = pDialog;
					}
					this.initializeStagedRowInputs(tmpView, tmpGroup, tmpStagedIndex);
				},
			}).then((pResult) =>
			{
				this.handleEntryModalResult(pResult);
			});
		return true;
	}

	/**
	 * Compose a stacked, labeled "one record" template for a TABULAR group.
	 *
	 * The group's own SectionTabularRowTemplateHash emits bare <td>s (invalid
	 * inside a modal <div>, and carrying no labels -- those live in the <th>s), so
	 * it cannot be reused the way the RecordSet layout's can. Instead re-run the
	 * same input-metatemplate composition the layout does, wrapped in our own
	 * field markup. Every input template is reused verbatim, so Picker,
	 * MaterialsEntitySelector, Signature, Diagram etc. render exactly as inline.
	 *
	 * Rebuilt on every open -- it is cheap string work and it picks up
	 * DynamicColumns changes for free.
	 *
	 * @param {Object} pView - The form view.
	 * @param {Object} pGroup - The tabular group descriptor.
	 * @param {Object} pConfig - The normalized EntryControls config.
	 * @return {string} - The registered template hash.
	 */
	buildModalRowTemplate(pView, pGroup, pConfig)
	{
		const tmpGenerator = this.pict.providers.MetatemplateGenerator;
		const tmpManifest = pGroup.supportingManifest;
		const tmpHash = `Headlight-Form-Template-TabularRow-Modal-${pView.options.Hash}-G${pGroup.GroupIndex}`;
		if (!tmpManifest || !Array.isArray(tmpManifest.elementAddresses))
		{
			this.pict.TemplateProvider.addTemplate(tmpHash, '');
			return tmpHash;
		}

		// ColumnChooser-hidden columns are SHOWN in the modal (marked with a note):
		// hiding is a transient view preference, and a hidden-but-required column
		// the user cannot fill is a dead end. TabularHidden is structural and is skipped.
		let tmpChooserHiddenSet = null;
		const tmpLayout = this.pict.providers['Pict-Layout-Tabular'];
		if (tmpLayout && (typeof tmpLayout._getTabularColumnChooserHiddenSet === 'function'))
		{
			try
			{
				tmpChooserHiddenSet = tmpLayout._getTabularColumnChooserHiddenSet(pView, pGroup);
			}
			catch (pError)
			{
				tmpChooserHiddenSet = null;
			}
		}

		let tmpTemplate = '<div class="columns is-multiline">';
		for (let k = 0; k < tmpManifest.elementAddresses.length; k++)
		{
			const tmpKey = tmpManifest.elementAddresses[k];
			const tmpInput = tmpManifest.elementDescriptors[tmpKey];
			if (!tmpInput || !tmpInput.PictForm || tmpInput.PictForm.TabularHidden)
			{
				continue;
			}
			// Same index stamping the layouts perform on bake -- the input
			// templates read PictForm.InputIndex.
			tmpInput.PictForm.InputIndex = k;
			tmpInput.PictForm.GroupIndex = pGroup.GroupIndex;
			// Read back by the {~EntryModalCellClass:~} macro below.
			tmpInput.PictForm._EntryModalHalfWidth = (pConfig.ModalColumns === 2);
			tmpInput.PictForm._EntryModalChooserHidden = !!(tmpChooserHiddenSet &&
				(typeof tmpChooserHiddenSet.has === 'function') && tmpChooserHiddenSet.has(String(tmpKey)));

			const tmpAddress = `getTabularRecordInput("${pGroup.GroupIndex}","${k}")`;
			tmpTemplate += tmpGenerator.getMetatemplateTemplateReference(pView, '-TabularTemplate-ModalCell-Prefix', tmpAddress);
			tmpTemplate += tmpGenerator.getTabularInputMetatemplateTemplateReference(pView, tmpInput.DataType,
				(tmpInput.PictForm.InputType || 'Default'), tmpAddress, pGroup.GroupIndex, k);
			tmpTemplate += tmpGenerator.getMetatemplateTemplateReference(pView, '-TabularTemplate-ModalCell-Postfix', tmpAddress);
		}
		tmpTemplate += '</div>';
		this.pict.TemplateProvider.addTemplate(tmpHash, tmpTemplate);
		return tmpHash;
	}

	/**
	 * Initialize the input widgets of the staged row only.
	 *
	 * Deliberately NOT pView.runInputProviderFunctions: that walks every group and
	 * every non-tabular input in the section, re-initializing widgets that are
	 * already live and logging out-of-bounds errors for sibling groups whose
	 * record sets are shorter than this row index. This is its tabular branch,
	 * scoped to one group and one row.
	 *
	 * @param {Object} pView - The form view.
	 * @param {Object} pGroup - The group descriptor.
	 * @param {number} pRowIndex - The staged row index.
	 * @return {void}
	 */
	initializeStagedRowInputs(pView, pGroup, pRowIndex)
	{
		const tmpManifest = pGroup.supportingManifest;
		if (!tmpManifest || !Array.isArray(tmpManifest.elementAddresses))
		{
			return;
		}
		// TransactionTracking is a runtime service on the pict instance that the
		// published pict types do not describe.
		const tmpPict = /** @type {any} */ (this.pict);
		const tmpTransactionGUID = this.fable.getUUID();
		const tmpTransaction = tmpPict.TransactionTracking.registerTransaction(tmpTransactionGUID);
		for (let k = 0; k < tmpManifest.elementAddresses.length; k++)
		{
			const tmpInput = tmpManifest.elementDescriptors[tmpManifest.elementAddresses[k]];
			if (!tmpInput || !tmpInput.PictForm || tmpInput.PictForm.TabularHidden)
			{
				continue;
			}
			const tmpProviderList = pView.getInputProviderList(tmpInput);
			for (let l = 0; l < tmpProviderList.length; l++)
			{
				const tmpProvider = this.pict.providers[tmpProviderList[l]];
				if (!tmpProvider || (typeof tmpProvider.onInputInitializeTabular !== 'function'))
				{
					continue;
				}
				const tmpValueAddress = this.pict.providers.Informary.getComposedContainerAddress(
					tmpInput.PictForm.InformaryContainerAddress, pRowIndex, tmpInput.PictForm.InformaryDataAddress);
				const tmpValue = pView.sectionManifest.getValueByHash(pView.getMarshalDestinationObject(), tmpValueAddress);
				try
				{
					if (tmpPict.TransactionTracking.checkEvent(tmpTransaction.TransactionKey,
						`TI${tmpInput.Hash}-P${tmpProviderList[l]}-R${pRowIndex}`, 'onInputInitialize'))
					{
						tmpProvider.onInputInitializeTabular(pView, pGroup, tmpInput, tmpValue,
							tmpInput.Macro.HTMLSelectorTabular, pRowIndex, tmpTransactionGUID);
					}
				}
				catch (pError)
				{
					this.pict.log.error(`Entry modal failed to initialize provider [${tmpProviderList[l]}] for input [${tmpInput.Hash}]: ${pError}`);
				}
			}
		}
		// Push the staged record's values (defaults, and anything a solver has
		// already produced) into the freshly rendered modal inputs.
		this.pict.providers.Informary.marshalDataToForm(pView.getMarshalDestinationObject(), pView.formID, pView.sectionManifest);
	}

	/**
	 * Resolve the entry modal: commit the staged row, or roll it back.
	 * @param {string|null} pResult - The clicked button hash, or null when dismissed.
	 * @return {boolean} - True when a row was committed.
	 */
	handleEntryModalResult(pResult)
	{
		const tmpState = this._modalEntryState;
		if (!tmpState)
		{
			return false;
		}
		this._modalEntryState = null;

		// pict-section-modal resolves the promise SYNCHRONOUSLY but only removes
		// the dialog from the DOM 220ms later. Empty the body now: the commit
		// render below paints inline row N with the same element ids and the same
		// data-i-index, and we must not let the two coexist even briefly.
		this.teardownEntryModalBody(tmpState.Dialog);

		const tmpView = this.pict.views[tmpState.ViewHash];
		const tmpGroup = tmpView ? tmpView.getGroup(tmpState.GroupIndex) : null;
		if (!tmpView || !tmpGroup)
		{
			return false;
		}
		const tmpConfig = this.getEntryControlsConfig(tmpGroup);
		const tmpCommitted = ((pResult === 'done') || (pResult === 'done-goto'));

		if (!tmpCommitted)
		{
			this.rollbackStagedEntry(tmpView, tmpGroup, tmpState.StagedIndex);
		}

		// Any rebuild the marshal guard swallowed while the modal was open has to
		// happen exactly once, now that the DOM and the model agree again.
		this.flushDeferredEntryRebuild(tmpView, tmpGroup);

		// Reorder BEFORE reconciling so a prepend costs one render, not two. The
		// reorder's own repaint marshals into a table that is still one row short;
		// that is harmless (Informary addresses rows by data-i-index and simply
		// finds nothing for the staged index) and the reconcile below repaints it.
		let tmpFinalIndex = tmpState.StagedIndex;
		if (tmpCommitted)
		{
			tmpFinalIndex = this.moveStagedEntryToStart(tmpView, tmpState.GroupIndex, tmpConfig, tmpState.StagedIndex);
		}

		this.reconcileRecordSet(tmpView, tmpGroup);
		this.prepareAnyExtraEntryControls(tmpGroup);

		if (tmpCommitted && (pResult === 'done-goto'))
		{
			this.scrollEntryIntoView(tmpState.ViewHash, tmpState.GroupIndex, tmpFinalIndex);
		}
		return tmpCommitted;
	}

	/**
	 * Remove the modal-hosted inputs from the document immediately.
	 * @param {any} pDialog - The pict-section-modal dialog element.
	 * @return {void}
	 */
	teardownEntryModalBody(pDialog)
	{
		if (!pDialog || (typeof pDialog.querySelector !== 'function'))
		{
			return;
		}
		const tmpBody = pDialog.querySelector('.pict-entry-modal-body') || pDialog.querySelector('.pict-modal-dialog-body');
		if (!tmpBody)
		{
			return;
		}
		while (tmpBody.firstChild)
		{
			tmpBody.removeChild(tmpBody.firstChild);
		}
	}

	/**
	 * Remove a cancelled staged entry from the record set.
	 *
	 * Deliberately NOT deleteDynamicTableRow: that refuses once the array is at
	 * MinimumRowCount, and the staged row is exactly what pushed it over. Splicing
	 * returns to the pre-add count, which was already a legal count.
	 *
	 * @param {Object} pView - The form view.
	 * @param {Object} pGroup - The group descriptor.
	 * @param {number} pStagedIndex - The staged row index.
	 * @return {boolean} - True when a row was removed.
	 */
	rollbackStagedEntry(pView, pGroup, pStagedIndex)
	{
		const tmpRecordSet = this.getEntryRecordSet(pView, pGroup);
		if (!tmpRecordSet || (pStagedIndex < 0) || (pStagedIndex >= tmpRecordSet.length))
		{
			return false;
		}
		tmpRecordSet.splice(pStagedIndex, 1);
		const tmpTabular = this.pict.providers.DynamicTabularData;
		if (tmpTabular && (typeof tmpTabular._spliceDependentPositionalColumns === 'function'))
		{
			try
			{
				tmpTabular._spliceDependentPositionalColumns(pGroup.RecordSetAddress, pStagedIndex, pStagedIndex + 1);
			}
			catch (pError)
			{
				this.pict.log.error(`Entry modal rollback could not splice dependent positional columns: ${pError}`);
			}
		}
		return true;
	}

	/**
	 * Run the one rebuild the modal-entry marshal guard suppressed, if any.
	 * @param {Object} pView - The form view.
	 * @param {Object} pGroup - The group descriptor.
	 * @return {void}
	 */
	flushDeferredEntryRebuild(pView, pGroup)
	{
		if (!pGroup || !pGroup._HeadlightModalDeferredRebuild)
		{
			return;
		}
		pGroup._HeadlightModalDeferredRebuild = false;
		const tmpLayout = this.pict.providers['Pict-Layout-Tabular'];
		if (tmpLayout && (typeof tmpLayout._rebuildTabularGroupView === 'function'))
		{
			tmpLayout._rebuildTabularGroupView(pView);
		}
	}

	/**
	 * Repaint + resolve everything bound to a group's record set. Mirrors the tail
	 * of DynamicTabularData.createDynamicTableRow: render the bound views,
	 * rebuild dependent DynamicColumns views, solve, marshal.
	 * @param {Object} pView - The form view.
	 * @param {Object} pGroup - The group descriptor.
	 * @return {void}
	 */
	reconcileRecordSet(pView, pGroup)
	{
		const tmpAddress = pGroup.RecordSetAddress;
		const tmpTabular = this.pict.providers.DynamicTabularData;
		const tmpViews = this.pict.views.PictFormMetacontroller.filterViews((pCandidate) =>
		{
			if (!pCandidate.isPictSectionForm)
			{
				return false;
			}
			const tmpGroups = pCandidate.getGroups();
			for (let i = 0; i < tmpGroups.length; i++)
			{
				if (tmpGroups[i].RecordSetAddress === tmpAddress)
				{
					return true;
				}
			}
			return false;
		});

		// _renderViewsPreservingRecordSet snapshots/restores entity-selector values
		// across the re-render; a plain render loses them ("the Source I just
		// picked disappeared after adding a row").
		if (tmpTabular && (typeof tmpTabular._renderViewsPreservingRecordSet === 'function'))
		{
			tmpTabular._renderViewsPreservingRecordSet(tmpAddress, tmpViews);
		}
		else
		{
			this.pict.log.warn('DynamicTabularData._renderViewsPreservingRecordSet is unavailable; falling back to a plain render (entity-selector values may be clobbered).');
			for (let i = 0; i < tmpViews.length; i++)
			{
				tmpViews[i].render();
			}
		}
		if (tmpTabular && (typeof tmpTabular._rebuildDependentDynamicColumnViews === 'function'))
		{
			tmpTabular._rebuildDependentDynamicColumnViews(tmpAddress);
		}
		this.pict.providers.DynamicSolver.solveViews();
		this.pict.views.PictFormMetacontroller.marshalFormSections();
	}

	/**
	 * Scroll a group's entry into view, but only when it is not already visible.
	 * @param {string} pViewHash - The form view hash.
	 * @param {number|string} pGroupIndex - The group index.
	 * @param {number} pRowIndex - The entry index.
	 * @return {boolean} - True when the entry element was found.
	 */
	scrollEntryIntoView(pViewHash, pGroupIndex, pRowIndex)
	{
		if (typeof document === 'undefined')
		{
			return false;
		}
		const tmpView = this.pict.views[pViewHash];
		const tmpGroup = tmpView ? tmpView.getGroup(pGroupIndex) : null;
		const tmpSelector = this.isRecordSetGroup(tmpGroup) ?
			`[data-recordset-group-index="${pGroupIndex}"][data-recordset-row-index="${pRowIndex}"]` :
			`tr[data-tabular-group-index="${pGroupIndex}"][data-tabular-row-index="${pRowIndex}"]`;
		const tmpElement = document.querySelector(tmpSelector);
		if (!tmpElement)
		{
			return false;
		}
		const tmpNavBar = document.getElementById('HeadlightPanels-NavBar');
		const tmpNavHeight = tmpNavBar ? tmpNavBar.offsetHeight : 60;
		const tmpRect = tmpElement.getBoundingClientRect();
		const tmpViewportHeight = window.innerHeight || 0;
		if ((tmpRect.top >= tmpNavHeight) && (tmpRect.bottom <= tmpViewportHeight))
		{
			return true;
		}
		window.scrollTo({ top: (tmpRect.top + window.scrollY) - tmpNavHeight - 10, behavior: 'smooth' });
		return true;
	}

	/**
	 * Reveal the inline/modal toggle on groups that have grown big enough to
	 * warrant the choice. Strictly read-all-then-write-all so the measurement
	 * never interleaves reads and writes, and STICKY -- once revealed a toggle is
	 * never hidden again, so a user who switched to modal entry does not lose the
	 * control when the row count drops back under the threshold.
	 *
	 * Called from prepareAnyExtraTableControls (every bake / add / delete) and
	 * from ResponsiveUIAdjustmentProvider.onWindowResize (already debounced), so
	 * it registers no listeners of its own.
	 *
	 * @return {void}
	 */
	refreshEntryModeToggleVisibility()
	{
		if (typeof document === 'undefined')
		{
			return;
		}
		const tmpToggles = document.querySelectorAll('[data-entry-mode-toggle]');
		if (!tmpToggles.length)
		{
			return;
		}
		const tmpViewportHeight = window.innerHeight || 0;
		const tmpToReveal = [];

		// PASS 1 -- reads only.
		for (let i = 0; i < tmpToggles.length; i++)
		{
			const tmpToggle = tmpToggles[i];
			if (!tmpToggle.classList.contains('is-hidden'))
			{
				continue;
			}
			const tmpContainer = tmpToggle.closest('.section-table-wrapper') || tmpToggle.closest('.section-recordset-wrapper');
			if (!tmpContainer)
			{
				continue;
			}
			const tmpRowThreshold = parseInt(tmpToggle.getAttribute('data-entry-mode-rows'), 10);
			const tmpViewportMultiple = parseFloat(tmpToggle.getAttribute('data-entry-mode-viewport'));
			// NOTE: the placeholder row's class really is 'table-emtpy-row' (sic) --
			// that spelling is what the group templates emit.
			let tmpEntryCount = tmpContainer.querySelectorAll('tbody tr:not(.table-emtpy-row)').length;
			if (!tmpEntryCount)
			{
				tmpEntryCount = tmpContainer.querySelectorAll('.RecordSetEntry').length;
			}
			const tmpHeight = tmpContainer.getBoundingClientRect().height;
			const tmpRevealByRows = (!isNaN(tmpRowThreshold) && (tmpRowThreshold > 0) && (tmpEntryCount >= tmpRowThreshold));
			const tmpRevealByHeight = (!isNaN(tmpViewportMultiple) && (tmpViewportMultiple > 0) && (tmpViewportHeight > 0) &&
				(tmpHeight >= (tmpViewportMultiple * tmpViewportHeight)));
			if (tmpRevealByRows || tmpRevealByHeight)
			{
				tmpToReveal.push(tmpToggle);
			}
		}

		// PASS 2 -- writes only.
		for (let i = 0; i < tmpToReveal.length; i++)
		{
			tmpToReveal[i].classList.remove('is-hidden');
		}
	}

	/**
	 * While a modal entry is staged the row exists in the model but deliberately
	 * NOT in the table DOM. The tabular layout's onDataMarshalToForm can rebuild
	 * mid-marshal (a DynamicColumns set change, or a ColumnChooser state-key
	 * drift), which would paint an inline twin of the modal's row carrying the
	 * same data-i-index. Defer that rebuild until the modal closes.
	 *
	 * Idempotent; no-op when the layout provider isn't registered.
	 * @return {void}
	 */
	overrideTabularMarshalForModalEntry()
	{
		const tmpLayout = this.pict.providers['Pict-Layout-Tabular'];
		if (!tmpLayout ||
			(typeof tmpLayout.onDataMarshalToForm !== 'function') ||
			tmpLayout._headlightModalEntryMarshalOverridden)
		{
			return;
		}
		const tmpSelf = this;
		tmpLayout._headlightModalEntryMarshalOverridden = true;
		const fOriginalMarshal = tmpLayout.onDataMarshalToForm;
		tmpLayout.onDataMarshalToForm = function(pView, pGroup)
		{
			const tmpState = tmpSelf._modalEntryState;
			if (tmpState && !tmpState.Committed && pView && pGroup &&
				(pView.Hash === tmpState.ViewHash) &&
				(Number(pGroup.GroupIndex) === Number(tmpState.GroupIndex)))
			{
				pGroup._HeadlightModalDeferredRebuild = true;
				return true;
			}
			return fOriginalMarshal.call(this, pView, pGroup);
		};
	}

	/**
	 * Generate the HTML for a group layout template.
	 * @param {Object} pGroup - The group object.
	 * @param {number} pGroupIndex - The index of the group.
	 * @param {string} pGroupKey - The key of the group.
	 * @param {Object} pGroupData - The data for the group.
	 * @param {number} pGroupDataIndex - The index of the group data.
	 * @param {string} pGroupDataKey - The key of the group data.
	 * @return {string} - The generated HTML for the group layout template.
	 */
	generateGroupLayoutTemplate(pGroup, pGroupIndex, pGroupKey, pGroupData, pGroupDataIndex, pGroupDataKey)
	{
		this.prepareAnyExtraTableControls();
		return '';
	}
}

module.exports = CustomInputHandler;
